import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import {
  PolicyHeaderOrderItem,
  PolicyHeaderDateFormat,
  PolicyHeaderIssuingAuthorityType,
  HeaderConfigurationResponse,
  PolicyHeaderFieldType,
  Attachment,
} from '../../../_types/manuals/draft';
import AttachmentPreviewBox from '../../_components/AttachmentPreviewBox';
import useManageAttachment from '../../_data_sources/useManageAttachment';
import { formatDatePreview } from '../../../_helpers/policyHeaderDateHelper';

interface HeaderLayoutPreviewProps {
  order: PolicyHeaderOrderItem[];
  headerConfig?: HeaderConfigurationResponse;
  dateFormat: PolicyHeaderDateFormat;
  issuingAuthorityType: PolicyHeaderIssuingAuthorityType;
  agencyLogoId?: string;
  issuingAuthoritySignatureId?: string;
  issuingAuthorityName?: string;
  isAgencyLogoLoading?: boolean;
  isIssuingAuthoritySignatureLoading?: boolean;
  manualId?: string;
  customFields?: Array<{ header: string; value: string; }>;
}

// Helper function to get display name for header types
const getHeaderDisplayName = (
  orderType: PolicyHeaderFieldType,
  customFields?: Array<{ header: string; value: string; }>
): string => {
  switch (orderType) {
    case 'EFFECTIVE_DATE':
      return t('draftManuals.manualProperties.policyHeaders.order.effectiveDate');
    case 'REVISED_DATE':
      return t('draftManuals.manualProperties.policyHeaders.order.revisedDate');
    case 'AGENCY_LOGO':
      return t('draftManuals.manualProperties.policyHeaders.order.agencyLogo');
    case 'ISSUING_AUTHORITY':
      return t('draftManuals.manualProperties.policyHeaders.order.issuingAuthority');
    case 'CUSTOM1':
      return customFields?.[0]?.header || t('draftManuals.manualProperties.policyHeaders.order.customField1');
    case 'CUSTOM2':
      return customFields?.[1]?.header || t('draftManuals.manualProperties.policyHeaders.order.customField2');
    default:
      return orderType;
  }
};

const HeaderLayoutPreview: React.FC<HeaderLayoutPreviewProps> = ({
  order,
  headerConfig,
  dateFormat,
  issuingAuthorityType,
  agencyLogoId,
  issuingAuthoritySignatureId,
  issuingAuthorityName,
  isAgencyLogoLoading,
  isIssuingAuthoritySignatureLoading,
  manualId,
  customFields,
}) => {
  const visibleItems = order.filter((item) => item.state === 'ENABLED');
  const { getAttachment: fetchAttachment } = useManageAttachment();

  const [agencyLogo, setAgencyLogo] = useState<Attachment | null>(null);
  const [issuingAuthoritySignature, setIssuingAuthoritySignature] = useState<Attachment | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);

  // Fetch agency logo when agencyLogoId changes
  useEffect(() => {
    if (agencyLogoId && manualId) {
      setLogoLoading(true);
      fetchAttachment({ manualId, attachmentId: agencyLogoId })
        .then((response) => {
          setAgencyLogo(response as any);
        })
        .catch((error) => {
          console.error('Failed to fetch agency logo:', error);
          setAgencyLogo(null);
        })
        .finally(() => {
          setLogoLoading(false);
        });
    } else {
      setAgencyLogo(null);
    }
  }, [agencyLogoId, manualId]);

  // Fetch issuing authority signature when issuingAuthoritySignatureId changes
  useEffect(() => {
    if (issuingAuthoritySignatureId && manualId) {
      setSignatureLoading(true);
      fetchAttachment({ manualId, attachmentId: issuingAuthoritySignatureId })
        .then((response) => {
          setIssuingAuthoritySignature(response as any);
        })
        .catch((error) => {
          console.error('Failed to fetch issuing authority signature:', error);
          setIssuingAuthoritySignature(null);
        })
        .finally(() => {
          setSignatureLoading(false);
        });
    } else {
      setIssuingAuthoritySignature(null);
    }
  }, [issuingAuthoritySignatureId, manualId]);

  return (
    <Box
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.default',
        minHeight: 200,
      }}
    >
      <Stack spacing={2}>
        {visibleItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('draftManuals.manualProperties.policyHeaders.order.noHeadersVisible')}
          </Typography>
        ) : (
          visibleItems.map((item, index) => (
            <Box
              key={item.type}
              sx={{
                p: 2,
                backgroundColor: 'background.paper',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {index + 1}. {getHeaderDisplayName(item.type, customFields ?? headerConfig?.customFields)}
              </Typography>
              {item.type === 'EFFECTIVE_DATE' && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formatDatePreview(dateFormat)}
                </Typography>
              )}
              {item.type === 'REVISED_DATE' && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formatDatePreview(dateFormat)}
                </Typography>
              )}
              {item.type === 'AGENCY_LOGO' && (
                <Box sx={{ mt: 1 }}>
                  <AttachmentPreviewBox
                    attachment={agencyLogo}
                    isLoading={logoLoading || isAgencyLogoLoading}
                    noImageText={t('draftManuals.manualProperties.policyHeaders.order.noLogoSelected')}
                  />
                </Box>
              )}
              {item.type === 'ISSUING_AUTHORITY' && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {/* Show signature image if type includes signature */}
                  {(issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'SIGNATURE_ONLY') && (
                    <Box>
                      <AttachmentPreviewBox
                        attachment={issuingAuthoritySignature}
                        isLoading={signatureLoading || isIssuingAuthoritySignatureLoading}
                        noImageText={t('draftManuals.manualProperties.policyHeaders.order.noSignatureSelected')}
                      />
                    </Box>
                  )}

                  {/* Show name if type includes name */}
                  {(issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'NAME_ONLY') && (
                    <Box>
                      {issuingAuthorityName ? (
                        <Typography variant="body2">
                          {issuingAuthorityName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          [{t('draftManuals.manualProperties.policyHeaders.order.noNameEntered')}]
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
              {(item.type === 'CUSTOM1' || item.type === 'CUSTOM2') && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  {(customFields ?? headerConfig?.customFields)?.[item.type === 'CUSTOM1' ? 0 : 1]?.value || '--'}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Stack>
    </Box>
  );
};

export default HeaderLayoutPreview;